import {observer} from "mobx-react";
import {useState, useMemo} from "react";
import styles from "./styles/AccordionTerrace.module.scss";
import FontAwesome from "../../../utilities/FontAwesome";
import Markdown from "../../../utilities/Markdown";
import dynamic from "next/dynamic";
import {SocialItemModalNavigationProps} from "../../../modals/SocialItemModal";
import * as React from "react";
import classNames from "classnames";
import {useStores} from "../../../../stores/useStores";

const AccordionItemEditor = dynamic(() => import('./AccordionItemEditor'))

const AccordionItem = observer((props: {
    item: IAccordionItem,
    index: number,
    blockObj: IAccordionTerrace,
    handleCancel: () => void,
    accordionList: IAccordionItem[],
    expanded: boolean,
    setExpanded: (value: Record<string | number, boolean>) => void,
    toggleExpansion: (id: number) => void,
} & SocialItemModalNavigationProps) => {
    const {userStore, modalStore, organizationStore} = useStores();
    const [editing, setEditing] = useState(false);
    const [addSubAccordion, setAddSubAccordion] = useState<boolean>(false);

    const isTopLevel = useMemo(() => {
        return props.item.path.split(".").length === 1;
    }, [])

    const accordionItemClassName = classNames({
        [styles.accordionItem]: true,
        [styles.accordionItemSubItem]: !isTopLevel,
    })

    function onKeyDown(event) {
        if (event.key === 'Enter') {
            props.toggleExpansion(props.item.id);
        }
        else if (event.key === 'Escape' && props.expanded) {
            props.toggleExpansion(props.item.id);
        }
    }

    const onClick = (e) => {
        e.stopPropagation();
        modalStore.addModal({
            type: "accordion",
            itemObj: props.item,
            getItemAtIndex: props?.getItemAtIndex,
            totalItemCount: props?.totalItemCount,
            initialItemIndex: props?.initialItemIndex,
        })
    }

    const addAccordionItem = (e) => {
        e.stopPropagation();
        setAddSubAccordion(true);
    }

    const handleCancel = () => {
        setAddSubAccordion(false);
    }

    if (editing) {
        return <div className={styles.accordionItem}>
            <AccordionItemEditor
                blockObj={props.blockObj}
                index={props.index}
                item={props.item}
                handleSave={() => setEditing(false)}
                handleCancel={() => {
                    props.handleCancel();
                    setEditing(false)
                }}
                accordionList={props.accordionList}
            />
        </div>
    } else {
        return <div className={accordionItemClassName}>
            <div onKeyDown={onKeyDown} tabIndex={0} onClick={(e) => { isTopLevel ?  props.toggleExpansion(props.item.id) :
                props.item.has_content ? onClick(e) : undefined } }>
                <div className={styles.accordionItemTitle}>
                    <div className={styles.fullWidthButton}>
                        <div>
                            <span>{props.item.title}</span>
                            {userStore.isEditor && !editing &&
                                <div className={styles.editItemButton}>
                                    <button key="edit" type="button" onClick={() => setEditing(!editing)}
                                            disabled={editing}
                                            title={editing ? "Click to save" : "Click to edit"}
                                            aria-label={editing ? "Click to save" : "Click to edit"}
                                            >
                                        <span className="fas fa-pencil-alt" role="presentation"/>
                                    </button>
                                    {organizationStore.organization.billing_products?.docmersion && !addSubAccordion && userStore.isEditor && isTopLevel &&
                                        <button
                                            style={{marginLeft: 10, marginBottom: 0}}
                                            title={"Add Accordion Sub Item"}
                                            className={`${styles.controlButton} ${styles.saveButton}`}
                                            aria-label={"Add Accordion Sub Item"}
                                            onClick={addAccordionItem}>
                                            <FontAwesome
                                                ariaHidden={false}
                                                prefix={'fas'}
                                                name={"fa-plus"}/>
                                            &nbsp;
                                            {"Add Sub-Item"}
                                        </button>}
                                </div>
                            }
                        </div>
                        {isTopLevel && <div className={styles.expandCollapseButton}>
                            {props.expanded ?
                                <span><FontAwesome prefix={"fas"} fixedWidth name={'fa-xmark'} ariaHidden/></span> :
                                <span className={styles.rotate}><FontAwesome prefix={"fas"} fixedWidth name={'fa-xmark'}
                                                                             ariaHidden/></span>}
                            {!props.expanded ?
                                <span><FontAwesome prefix={"fas"} fixedWidth name={'fa-chevron-down'}
                                                   ariaHidden/></span> :
                                <span className={styles.rotate}><FontAwesome prefix={"fas"} fixedWidth
                                                                             name={'fa-chevron-down'}
                                                                             ariaHidden/></span>}
                        </div>}
                    </div>
                </div>
                <div className={`${styles.accordionItemContent} ${props.expanded ? styles.open : styles.closed}`}>
                    <div className={styles.markDownWrapper}>
                        <Markdown allowLinks={true} withStyles={true}
                                   source={props.item.description}/>
                    </div>
                    {organizationStore.organization.billing_products?.docmersion &&
                        <div>
                            {props.item.has_content && <button className={styles.viewMoreButton}
                                                               title={"View Accordion Content"}
                                                               aria-label={"View Accordion Content"}
                                                               onClick={onClick}>
                                View More
                                <FontAwesome prefix={"fas"} name={"fa-arrow-right"} fixedWidth/>
                            </button>}
                    </div>}
                </div>
            </div>
            {addSubAccordion && <div className={styles.subItemEditor}>
                <AccordionItemEditor
                blockObj={props.blockObj}
                index={props.index}
                parentItem={props.item}
                handleSave={() => setAddSubAccordion(false)}
                handleCancel={() => {
                    setAddSubAccordion(false)
                }}
                accordionList={props.accordionList}
            />
            </div>}
            {organizationStore.organization.billing_products?.docmersion && props.expanded && props.item.children.length > 0 && props.item.children.map((item, index) => {
                const itemIndex = props.blockObj.items.findIndex(listItem => listItem.id === item.id);
                return <AccordionItem key={item.id}
                               totalItemCount={props.blockObj.items.length}
                               getItemAtIndex={idx => {
                                   return props.blockObj.items.length >= idx ? props.blockObj.items[idx] : null;
                               }}
                               initialItemIndex={itemIndex}
                               item={item}
                               accordionList={props.item.children}
                               index={itemIndex}
                               blockObj={props.blockObj}
                               handleCancel={handleCancel}
                               expanded={true}
                               setExpanded={props.setExpanded}
                               toggleExpansion={props.toggleExpansion}
                />
            })}
        </div>
    }
});

export default AccordionItem;
